// There's no great way of using react-router's Link with components from other
// libraries.
//
//     <Button component={Link} to="/some/path">Yo</Button>
//
// That fails type checking. So there's also:
//
//     <Link
//       component={({ navigate, ...props }) => (
//         <Button
//           // This gets the href prop
//           {...props}
//           // Need to pass onClick to do the actual navigation
//           onClick={navigate}
//         >
//           Yo
//         </Button>
//       )}
//     />
//
// but you'll need a another similar looking thing for IconButton, and anything
// else that takes an href or onClick that you'd like to use for navigation.
//
// LinkChild solves both by cloning the child and passing an optional href and
// optional onClick:
//
//     <LinkChild to="/some/path">
//       <Button>Yo</Button>
//     </LinkChild>

import { createLocation, Location, LocationDescriptor } from 'history'
import { Children, cloneElement } from 'react'
import { LinkProps, useHistory } from 'react-router-dom'

export interface LinkChildProps {
  to: LinkProps['to']
  replace?: boolean
  exact?: boolean

  onClick?: (event: React.MouseEvent<any>) => void
  className?: string
  activeClassName?: string
  activeStyle?: React.CSSProperties
  // This is a function because it needs to run when the route changes, and not
  // all components that use LinkChild are hooked up to the history.
  forceActive?: () => boolean

  disableHref?: boolean
  disableOnClick?: boolean
  value?: string | number
  style?: React.CSSProperties
  // This component explicitly expects only a single, ReactElement child that
  // takes dom-like props.
  children: React.ReactElement<{
    href?: string
    className?: string
    style?: React.CSSProperties
    onClick?: (event: React.MouseEvent<any>) => void
    value?: string | number
  }>
}

/** Type-safe version of react-router's NavLink using children. */
const LinkChild: React.FC<LinkChildProps> = (props) => {
  const history = useHistory()
  // `useRouteMatch` has issues when sub routers are involved, so just use
  // `history.location`.
  const url = history.location.pathname

  if (!props.children) {
    return null
  }

  const location = normalizeToLocation(
    resolveToLocation(props.to, history.location),
    history.location,
  )
  const href = history.createHref(location)

  let className = props.className || ''
  let style = props.style

  // If `activeClassName` or `activeStyle` are set then check the current
  // location against our `to` prop.
  if (props.activeClassName || props.activeStyle) {
    let doesMatch = false
    if (props.forceActive) {
      doesMatch = props.forceActive()
    } else {
      doesMatch = props.exact
        ? url === href.replace(/^#/, '')
        : url.startsWith(href.replace(/^#/, ''))
    }

    if (doesMatch) {
      if (props.activeClassName) {
        className = `${className} ${props.activeClassName}`
      }
      if (props.activeStyle) {
        style = { ...style, ...props.activeStyle }
      }
    }
  }

  const handleClick = (event: React.MouseEvent) => {
    if (event.isPropagationStopped()) {
      return
    }

    props.onClick?.(event)
    event.preventDefault()

    // Allow onClick prop to stop propagation
    if (event.isPropagationStopped()) {
      return
    }

    event.stopPropagation()

    // Open a new window on middle click and ctrl click.
    if (event.metaKey || event.ctrlKey || event.button === 1) {
      window.open(href)
      return
    }

    if (props.replace) {
      history.replace(location)
    } else {
      history.push(location)
    }
  }

  const childProps = {
    className,
    style,
    href: props.disableHref ? undefined : href,
    onClick: props.disableOnClick ? undefined : handleClick,
    value: props.value,
  }
  // Return the child element with extra props.
  return cloneElement(Children.only(props.children), childProps)
}

export default LinkChild

export interface LegacyLinkChildProps extends Omit<LinkChildProps, 'to'> {
  to: string
  isLegacy?: boolean
}

export const LegacyLinkChild: React.FC<LegacyLinkChildProps> = ({
  isLegacy,
  ...rest
}) => {
  if (isLegacy) {
    const childProps = {
      href: rest.to,
      onclick: rest.onClick,
      className: rest.className,
      style: rest.style,
      value: rest.value,
    }
    // Return the child element with extra props.
    return cloneElement(Children.only(rest.children), childProps)
  }

  return <LinkChild {...rest} />
}

// https://github.com/ReactTraining/react-router/blob/f31bb27aa61dd4cb1c3cd9aa78133f739e2e9bb9/packages/react-router-dom/modules/utils/locationUtils.js#L3
const resolveToLocation = (
  to: LinkChildProps['to'],
  currentLocation: Location,
) => {
  return typeof to === 'function' ? to(currentLocation) : to
}

// https://github.com/ReactTraining/react-router/blob/f31bb27aa61dd4cb1c3cd9aa78133f739e2e9bb9/packages/react-router-dom/modules/utils/locationUtils.js#L6
const normalizeToLocation = (
  to: LocationDescriptor,
  currentLocation: Location,
) => {
  return typeof to === 'string'
    ? createLocation(to, undefined, undefined, currentLocation)
    : to
}
